import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  Box,
  Button as MuiButton,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Container, Button } from "reactstrap";
import { SnackbarProvider, enqueueSnackbar } from "notistack";

import { SupportedPlatforms } from "./FileUpload";
import loadingGif from "./../../../assets/images/aws/loadingGif.gif";
import {
  getIaacLicenseRequest,
  getIaacSubscriptionRequest,
  postIaacSubscriptionRequest,
  putIaacSubscriptionRequest,
  clearAllIaacSubscriptionMessages,
} from "../../../store/actions";
import {
  ACCOUNT_DETAIL_FONT_FAMILY,
  ACCOUNT_DETAIL_FONT_SX,
} from "../../../components/AccountDetailPolicy/accountDetailTypography";

export const subscriptionNames = ["Basic", "Standard", "Premium", "Enterprise"];

/** Display rank for license cards (unknown names last). */
const SUBSCRIPTION_SORT_ORDER = {
  Basic: 0,
  "AWS Marketplace": 1,
  Standard: 2,
  Premium: 3,
  Enterprise: 4,
};

/** Upgrade rank used only for first-time (create) selection. */
const SUBSCRIPTION_UPGRADE_ORDER = {
  Basic: 0,
  "AWS Marketplace": 0,
  Standard: 1,
  Premium: 2,
  Enterprise: 3,
};

const getUpgradeRank = (subscriptionName) => {
  if (subscriptionName in SUBSCRIPTION_UPGRADE_ORDER) {
    return SUBSCRIPTION_UPGRADE_ORDER[subscriptionName];
  }
  return -1;
};

const formatUploadLimit = (limit) => {
  if (limit === -1 || limit === "-1") return "Unlimited";
  return String(limit);
};

const BRAND_ORANGE = "#F98125";

const LicenseSelection = (props) => {
  const { orgcode } = props;
  const {
    licenseSelectionMode,
    setPage,
    currentSubscription,
    setSubscriptionUpdateTime,
  } = props;
  const isViewOnly = licenseSelectionMode === "update";
  const [previousSubscription, setPreviousSubscription] =
    useState(currentSubscription);
  const [userSelectedLicense, setUserSelectedLicense] = useState(null);

  useEffect(() => {
    if (orgcode) {
      props.getIaacLicense(orgcode);
    }
  }, [orgcode]);

  const canUpgradeSubscription = (selectedSubscription) => {
    if (!currentSubscription?.subscription || !selectedSubscription?.subscription) {
      return true;
    }
    return (
      getUpgradeRank(selectedSubscription.subscription) >
      getUpgradeRank(currentSubscription.subscription)
    );
  };

  const handleLicenseClick = (subscriptionRequest) => {
    if (isViewOnly) return;
    if (licenseSelectionMode === "create" || canUpgradeSubscription(subscriptionRequest)) {
      setUserSelectedLicense(subscriptionRequest);
    } else {
      enqueueSnackbar("Downgrading subscription is not allowed.", {
        variant: "error",
      });
    }
  };

  const handleLicense = () => {
    if (isViewOnly || !userSelectedLicense) return;

    const subscriptionRequest = {
      subscription: userSelectedLicense.subscription,
      price: userSelectedLicense.price,
      orgCode: orgcode,
      limit_value: userSelectedLicense.limit,
    };

    if (licenseSelectionMode === "create") {
      props.postIaacSubscription(orgcode, subscriptionRequest);
    } else if (canUpgradeSubscription(userSelectedLicense)) {
      props.putIaacSubscription(orgcode, subscriptionRequest);
    } else {
      enqueueSnackbar("Downgrading subscription is not allowed.", {
        variant: "error",
      });
    }
  };

  const postIaacSubscriptionMessage = props.postIaacSubscriptionMessage;
  const postIaacSubscriptionError = props.postIaacSubscriptionError;
  const putIaacSubscriptionMessage = props.putIaacSubscriptionMessage;
  const putIaacSubscriptionError = props.putIaacSubscriptionError;

  useEffect(() => {
    if (postIaacSubscriptionMessage) {
      enqueueSnackbar(postIaacSubscriptionMessage, { variant: "success" });
      setSubscriptionUpdateTime(new Date().getTime());
      setPage("dashboard");
    }
    if (putIaacSubscriptionMessage) {
      enqueueSnackbar(putIaacSubscriptionMessage, { variant: "success" });
      props.getIaacSubscription(orgcode);
    }
    if (postIaacSubscriptionError) {
      enqueueSnackbar(postIaacSubscriptionError, { variant: "error" });
    }
    if (putIaacSubscriptionError) {
      enqueueSnackbar(putIaacSubscriptionError, { variant: "error" });
    }
    setTimeout(() => props.clearAllIaacSubscriptionMessages(), 10 * 1000);
  }, [
    orgcode,
    postIaacSubscriptionMessage,
    postIaacSubscriptionError,
    putIaacSubscriptionMessage,
    putIaacSubscriptionError,
    props,
    setPage,
    setSubscriptionUpdateTime,
  ]);

  useEffect(() => {
    if (props.subscription && licenseSelectionMode === "update") {
      if (
        props.subscription.subscription !== previousSubscription?.subscription
      ) {
        setPreviousSubscription(props.subscription);
        setSubscriptionUpdateTime(new Date().getTime());
        setPage("dashboard");
      }
    }
  }, [
    props.subscription,
    previousSubscription,
    licenseSelectionMode,
    setPage,
    setSubscriptionUpdateTime,
  ]);

  const sortedSubscriptions = useMemo(() => {
    if (!props.license?.subscriptions) return [];
    return props.license.subscriptions.slice().sort((a, b) => {
      const aOrder = SUBSCRIPTION_SORT_ORDER[a.subscription] ?? 99;
      const bOrder = SUBSCRIPTION_SORT_ORDER[b.subscription] ?? 99;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return String(a.subscription).localeCompare(String(b.subscription));
    });
  }, [props.license]);

  const currentName = currentSubscription?.subscription;

  return (
    <>
      <SnackbarProvider />
      <div className="page-content" style={{ backgroundColor: "#fff" }}>
        <Container fluid>
          <Box sx={{ ...ACCOUNT_DETAIL_FONT_SX, pb: 5, px: { xs: 0, md: 1 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                mb: 3.5,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1.25,
                    mb: 0.75,
                  }}
                >
                  <Typography
                    component="h2"
                    sx={{
                      fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
                      fontWeight: 700,
                      fontSize: "1.5rem",
                      color: "#0f172a",
                      letterSpacing: "-0.025em",
                      lineHeight: 1.2,
                      m: 0,
                    }}
                  >
                    {isViewOnly ? "License Plans" : "License Selection"}
                  </Typography>
                  {isViewOnly && currentSubscription && (
                    <Chip
                      label={`Current: ${currentName}`}
                      size="small"
                      sx={{
                        fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
                        fontWeight: 600,
                        height: 26,
                        backgroundColor: "#fff7ed",
                        color: BRAND_ORANGE,
                        border: `1px solid ${BRAND_ORANGE}`,
                      }}
                    />
                  )}
                </Box>
                <Typography
                  sx={{
                    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
                    color: "#64748b",
                    fontSize: "0.875rem",
                    fontWeight: 400,
                    maxWidth: 520,
                  }}
                >
                  {isViewOnly
                    ? "Review available IaC Scanning plans for your organization. License changes are managed by your administrator."
                    : "Choose a plan to enable IaC Scanning for your organization."}
                </Typography>
              </Box>

              {isViewOnly && (
                <Button
                  style={{
                    backgroundColor: BRAND_ORANGE,
                    borderColor: BRAND_ORANGE,
                    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                  size="sm"
                  onClick={() => setPage("dashboard")}
                >
                  Show IaC Dashboard
                </Button>
              )}
            </Box>

            {props.isIaacLicenseGetting && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 8,
                }}
              >
                <img src={loadingGif} height={40} alt="Loading Spinner" />
              </Box>
            )}

            {props.license && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  gap: 3,
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    width: "100%",
                    gap: { xs: 1.5, sm: 2 },
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      md: "repeat(3, minmax(0, 1fr))",
                      lg: "repeat(5, minmax(0, 1fr))",
                    },
                  }}
                >
                  {sortedSubscriptions.map((subscription) => {
                    const isCurrent =
                      isViewOnly && currentName === subscription.subscription;
                    const isPicked =
                      !isViewOnly &&
                      userSelectedLicense?.subscription ===
                        subscription.subscription;

                    return (
                      <Box
                        key={subscription.subscription}
                        onClick={
                          isViewOnly
                            ? undefined
                            : () => handleLicenseClick(subscription)
                        }
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                          minHeight: { xs: 140, lg: 180 },
                          p: { xs: 2, lg: 2.25 },
                          borderRadius: "12px",
                          border: "1.5px solid",
                          borderColor: isCurrent
                            ? BRAND_ORANGE
                            : isPicked
                              ? "#fdba74"
                              : "#e2e8f0",
                          backgroundColor: isCurrent
                            ? "#fff7ed"
                            : isPicked
                              ? "#fffbeb"
                              : "#ffffff",
                          boxShadow: isCurrent
                            ? "0 4px 16px rgba(249, 129, 37, 0.16)"
                            : "0 1px 2px rgba(15, 23, 42, 0.04)",
                          cursor: isViewOnly ? "default" : "pointer",
                          transition:
                            "border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease",
                          position: "relative",
                          "&:hover": isViewOnly
                            ? undefined
                            : {
                                borderColor: isPicked ? "#fdba74" : "#cbd5e1",
                                boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
                              },
                        }}
                      >
                        {isCurrent && (
                          <Chip
                            icon={
                              <CheckCircleIcon
                                sx={{ fontSize: "15px !important" }}
                              />
                            }
                            label="Active"
                            size="small"
                            sx={{
                              position: "absolute",
                              top: 12,
                              right: 12,
                              height: 24,
                              fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
                              fontWeight: 600,
                              fontSize: "0.6875rem",
                              backgroundColor: BRAND_ORANGE,
                              color: "#fff",
                              "& .MuiChip-icon": { color: "#fff" },
                            }}
                          />
                        )}

                        <Stack spacing={2} sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
                              fontWeight: 700,
                              fontSize: "1.0625rem",
                              color: "#0f172a",
                              letterSpacing: "-0.02em",
                              pr: isCurrent ? 7 : 0,
                              lineHeight: 1.3,
                            }}
                          >
                            {subscription.subscription}
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                              pt: 0.5,
                            }}
                          >
                            <CheckCircleIcon
                              sx={{
                                fontSize: 18,
                                mt: "1px",
                                color: isCurrent ? BRAND_ORANGE : "#16a34a",
                                flexShrink: 0,
                              }}
                            />
                            <Box>
                              <Typography
                                sx={{
                                  fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
                                  fontSize: "0.6875rem",
                                  fontWeight: 600,
                                  letterSpacing: "0.04em",
                                  textTransform: "uppercase",
                                  color: "#94a3b8",
                                  mb: 0.25,
                                }}
                              >
                                Upload limit
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
                                  fontSize: "1.125rem",
                                  fontWeight: 700,
                                  color: "#0f172a",
                                  letterSpacing: "-0.02em",
                                }}
                              >
                                {formatUploadLimit(subscription.limit)}
                              </Typography>
                            </Box>
                          </Box>

                          {!isViewOnly && (
                            <MuiButton
                              variant="contained"
                              disableElevation
                              disabled={props.isIaacSubscriptionPosting}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleLicenseClick(subscription);
                              }}
                              sx={{
                                mt: "auto",
                                textTransform: "none",
                                fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
                                fontWeight: 600,
                                fontSize: "0.8125rem",
                                borderRadius: "8px",
                                py: 0.85,
                                backgroundColor: isPicked
                                  ? "#ea6c0c"
                                  : BRAND_ORANGE,
                                "&:hover": { backgroundColor: "#ea6c0c" },
                                "&.Mui-disabled": {
                                  backgroundColor: "#fed7aa",
                                  color: "#fff",
                                  opacity: 0.7,
                                },
                              }}
                            >
                              {isPicked ? "Selected" : "Select"}
                            </MuiButton>
                          )}
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>

                {!isViewOnly && (
                  <MuiButton
                    variant="contained"
                    disableElevation
                    onClick={handleLicense}
                    disabled={
                      !userSelectedLicense ||
                      props.isIaacSubscriptionPosting ||
                      props.isIaacSubscriptionPutting
                    }
                    sx={{
                      textTransform: "none",
                      fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      borderRadius: "8px",
                      px: 4,
                      py: 1,
                      minWidth: 140,
                      backgroundColor: BRAND_ORANGE,
                      "&:hover": { backgroundColor: "#ea6c0c" },
                      "&.Mui-disabled": {
                        backgroundColor: "#fed7aa",
                        color: "#fff",
                        opacity: 0.75,
                      },
                    }}
                  >
                    Submit
                  </MuiButton>
                )}
              </Box>
            )}

            {licenseSelectionMode === "create" && <SupportedPlatforms />}
          </Box>
        </Container>
      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  const {
    isIaacLicenseGetting,
    getIaacLicenseMessage,
    getIaacLicenseError,
    license,
  } = state.iaacLicense;

  const {
    isIaacSubscriptionPosting,
    postIaacSubscriptionMessage,
    postIaacSubscriptionError,
    isIaacSubscriptionPutting,
    putIaacSubscriptionMessage,
    putIaacSubscriptionError,
    isIaacSubscriptionGetting,
    getIaacSubscriptionMessage,
    getIaacSubscriptionError,
    subscription,
  } = state.iaacSubscription;

  return {
    isIaacLicenseGetting,
    getIaacLicenseMessage,
    getIaacLicenseError,
    isIaacSubscriptionPosting,
    postIaacSubscriptionMessage,
    postIaacSubscriptionError,
    isIaacSubscriptionPutting,
    putIaacSubscriptionMessage,
    putIaacSubscriptionError,
    license,
    isIaacSubscriptionGetting,
    getIaacSubscriptionMessage,
    getIaacSubscriptionError,
    subscription,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getIaacLicense: (orgcode) => {
      dispatch(getIaacLicenseRequest(orgcode));
    },
    postIaacSubscription: (orgcode, subscriptionRequest) => {
      dispatch(postIaacSubscriptionRequest(orgcode, subscriptionRequest));
    },
    putIaacSubscription: (orgcode, subscriptionRequest) => {
      dispatch(putIaacSubscriptionRequest(orgcode, subscriptionRequest));
    },
    getIaacSubscription: (orgcode) => {
      dispatch(getIaacSubscriptionRequest(orgcode));
    },
    clearAllIaacSubscriptionMessages: () => {
      dispatch(clearAllIaacSubscriptionMessages());
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LicenseSelection);
